/**
 * Credit Purchase Dialog Component
 * Allows users to purchase credits with predefined or custom amounts
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddCardIcon from '@mui/icons-material/AddCard';
import { useApi } from '../../../hooks/useApi';

// Predefined credit amounts
const PRESET_AMOUNTS = [25, 50, 100, 200];
const MIN_AMOUNT = 10;

/**
 * CreditPurchaseDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {number} props.currentCredits - Current credit balance (for display)
 * @param {Array} props.paymentMethods - Available payment methods
 */
function CreditPurchaseDialog({ open, onClose, currentCredits = 0, paymentMethods = [] }) {
  const api = useApi();
  const queryClient = useQueryClient();

  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [error, setError] = useState(null);

  const defaultPaymentMethod = paymentMethods.find(pm => pm.isDefault);

  // Purchase mutation
  const purchaseMutation = useMutation({
    mutationFn: async (amount) => {
      return api.post('/api/billing/credits/purchase', {
        amount,
        paymentMethodId: defaultPaymentMethod?.id,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['billing']);
      onClose(data);
    },
    onError: (err) => {
      setError(err.message || 'Failed to purchase credits');
    },
  });

  const handleAmountChange = (event, newAmount) => {
    if (newAmount !== null) {
      setSelectedAmount(newAmount);
      setUseCustom(false);
      setError(null);
    }
  };

  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    setCustomAmount(value);
    setUseCustom(true);
    setSelectedAmount(null);
    setError(null);
  };

  const handlePurchase = () => {
    const amount = useCustom ? parseFloat(customAmount) : selectedAmount;

    if (isNaN(amount) || amount < MIN_AMOUNT) {
      setError(`Minimum purchase amount is $${MIN_AMOUNT}`);
      return;
    }

    if (!defaultPaymentMethod) {
      setError('Please add a payment method first');
      return;
    }

    purchaseMutation.mutate(amount);
  };

  const handleClose = () => {
    if (!purchaseMutation.isPending) {
      setError(null);
      setSelectedAmount(50);
      setCustomAmount('');
      setUseCustom(false);
      onClose();
    }
  };

  const finalAmount = useCustom ? parseFloat(customAmount) || 0 : selectedAmount || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { bgcolor: 'background.paper' } }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Add Credits
        <IconButton onClick={handleClose} size="small" disabled={purchaseMutation.isPending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 3
          }}>
          Current balance: <strong>${currentCredits.toFixed(2)}</strong>
        </Typography>

        {/* Preset Amounts */}
        <Typography variant="subtitle2" gutterBottom>
          Select Amount
        </Typography>
        <ToggleButtonGroup
          value={selectedAmount}
          exclusive
          onChange={handleAmountChange}
          fullWidth
          sx={{ mb: 3 }}
        >
          {PRESET_AMOUNTS.map((amount) => (
            <ToggleButton
              key={amount}
              value={amount}
              sx={{
                py: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.dark',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                },
              }}
            >
              ${amount}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Custom Amount */}
        <Typography variant="subtitle2" gutterBottom>
          Or Enter Custom Amount
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={customAmount}
          onChange={handleCustomAmountChange}
          placeholder={`Minimum $${MIN_AMOUNT}`}
          sx={{ mb: 3 }}
          slotProps={{
            input: {
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
            },

            htmlInput: {
              min: MIN_AMOUNT,
              step: 1,
            }
          }} />

        {/* Payment Method */}
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Payment Method
          </Typography>
          {defaultPaymentMethod ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCardIcon fontSize="small" />
              <Typography>
                {defaultPaymentMethod.brand?.toUpperCase()} ending in {defaultPaymentMethod.last4}
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No payment method on file. Please add a card first.
            </Alert>
          )}
        </Box>

        {/* Summary */}
        {finalAmount >= MIN_AMOUNT && (
          <Box sx={{ p: 2, bgcolor: 'primary.dark', borderRadius: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">Total to charge:</Typography>
              <Typography variant="h6" sx={{
                color: "primary.light"
              }}>
                ${finalAmount.toFixed(2)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1
              }}>
              New balance will be: ${(currentCredits + finalAmount).toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={purchaseMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePurchase}
          disabled={
            purchaseMutation.isPending ||
            finalAmount < MIN_AMOUNT ||
            !defaultPaymentMethod
          }
          startIcon={purchaseMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {purchaseMutation.isPending ? 'Processing...' : `Purchase $${finalAmount.toFixed(2)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreditPurchaseDialog;
